import type React from "react";
import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { createTheme } from "@mui/material/styles";
import { ThemeProvider } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import {
  exyboxGoldThemeOptions,
  forestGreenThemeOptions,
  oceanBlueThemeOptions,
  lightBlueThemeOptions,
  pastelThemeOptions,
  stoneThemeOptions,
  volcanoThemeOptions,
  sageThemeOptions,
  espressoThemeOptions,
} from "../themes";
import { DENSITY_TOKENS } from "../constants/density";
import { useDensity } from "./DensityContext";

const THEME_KEY = "exybox_theme";
const LOGIN_THEME_KEY = "exybox_login_theme";

const THEME_NAMES = ["exyboxGold", "forestGreen", "oceanBlue", "lightBlue", "pastel"] as const;

export type ThemeName =
  | "exyboxGold"
  | "forestGreen"
  | "oceanBlue"
  | "lightBlue"
  | "pastel"
  | "stone"
  | "volcano"
  | "sage"
  | "espresso";

export interface ThemeInfo {
  name: ThemeName;
  label: string;
  description: string;
  isDark: boolean;
}

// Separate light and dark themes
export const lightThemes: ThemeInfo[] = [
  {
    name: "lightBlue" as ThemeName,
    label: "Light Blue",
    description: "Soft baby blue theme",
    isDark: false,
  },
  {
    name: "pastel" as ThemeName,
    label: "Pastel",
    description: "Warm cream and sage green theme",
    isDark: false,
  },
  {
    name: "stone" as ThemeName,
    label: "Stone",
    description: "Elegant light gray theme",
    isDark: false,
  },
  {
    name: "sage" as ThemeName,
    label: "Sage",
    description: "Soft pastel green theme",
    isDark: false,
  },
].sort((a, b) => a.label.localeCompare(b.label));

export const darkThemes: ThemeInfo[] = [
  {
    name: "exyboxGold" as ThemeName,
    label: "Exybox Gold",
    description: "Dark theme with gold accents",
    isDark: true,
  },
  {
    name: "forestGreen" as ThemeName,
    label: "Forest Green",
    description: "Dark theme with green accents",
    isDark: true,
  },
  {
    name: "espresso" as ThemeName,
    label: "Espresso",
    description: "Dark theme with rich brown accents",
    isDark: true,
  },
  {
    name: "oceanBlue" as ThemeName,
    label: "Ocean Blue",
    description: "Dark theme with ocean blue accents",
    isDark: true,
  },
  {
    name: "volcano" as ThemeName,
    label: "Volcano",
    description: "Dark theme with volcanic accents",
    isDark: true,
  },
].sort((a, b) => a.label.localeCompare(b.label));

// Combined themes for backward compatibility
export const availableThemes: ThemeInfo[] = [...lightThemes, ...darkThemes];

const themeOptionsMap = {
  lightBlue: lightBlueThemeOptions,
  pastel: pastelThemeOptions,
  exyboxGold: exyboxGoldThemeOptions,
  forestGreen: forestGreenThemeOptions,
  oceanBlue: oceanBlueThemeOptions,
  stone: stoneThemeOptions,
  volcano: volcanoThemeOptions,
  sage: sageThemeOptions,
  espresso: espressoThemeOptions,
};

interface ThemeSwitcherContextProps {
  themeName: ThemeName;
  setThemeName: (name: ThemeName) => void;
  currentThemeInfo: ThemeInfo;
}

const ThemeSwitcherContext = createContext<ThemeSwitcherContextProps | undefined>(undefined);

export const ThemeSwitcherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";
  const { density } = useDensity();

  const [themeName, setThemeName] = useState<ThemeName>(() => {
    if (isLoginPage) {
      // Always randomize theme on login page
      const selectedTheme = THEME_NAMES[Math.floor(Math.random() * THEME_NAMES.length)];
      localStorage.setItem(LOGIN_THEME_KEY, selectedTheme);
      return selectedTheme || "volcano";
    }
    const stored = localStorage.getItem(THEME_KEY);
    const loginTheme = localStorage.getItem(LOGIN_THEME_KEY);
    return (stored as ThemeName) || loginTheme || "volcano";
  });

  // Effect to handle theme changes when navigating to/from login page
  useEffect(() => {
    if (isLoginPage) {
      // Always randomize theme on login page
      const selectedTheme = THEME_NAMES[Math.floor(Math.random() * THEME_NAMES.length)];
      localStorage.setItem(LOGIN_THEME_KEY, selectedTheme);
      setThemeName(selectedTheme || "volcano");
    } else {
      // When leaving login page, restore the theme from localStorage
      const stored = localStorage.getItem(THEME_KEY);
      if (stored) {
        setThemeName((stored as ThemeName) || "volcano");
      }
    }
  }, [isLoginPage]);

  const currentThemeInfo =
    availableThemes.find((theme) => theme.name === themeName) || availableThemes[0];

  useEffect(() => {
    // Only update localStorage if not on login page
    if (!isLoginPage) {
      localStorage.setItem(THEME_KEY, themeName);
    }
  }, [themeName, isLoginPage]);

  const theme = useMemo(() => {
    const tokens = DENSITY_TOKENS[density];
    const baseOptions = themeOptionsMap[themeName];
    return createTheme({
      ...baseOptions,
      typography: { ...baseOptions.typography, fontSize: tokens.typographyFontSize },
      spacing: tokens.spacing,
      density: {
        level: density,
        tableCellPaddingY: tokens.tableCellPaddingY,
        tableHeadPaddingY: tokens.tableHeadPaddingY,
      },
    });
  }, [themeName, density]);

  return (
    <ThemeSwitcherContext.Provider value={{ themeName, setThemeName, currentThemeInfo }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeSwitcherContext.Provider>
  );
};

export const useThemeSwitcher = () => {
  const ctx = useContext(ThemeSwitcherContext);
  if (!ctx) throw new Error("useThemeSwitcher must be used within ThemeSwitcherProvider");
  return ctx;
};
